import * as React from 'react';
import { Input, Button, Loading } from 'sha-el-design';
import { FaUser, FaPassport } from 'react-icons/fa';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { cssRaw } from 'typestyle';
import { userSchema } from './constant';
import { UserService } from '../../service/userService';
import { Inject } from '../../helpers';
import { History } from 'history';

// Brand assets. file-loader (configs/webpack/common.js) rewrites these to emitted
// URLs under img/, so they are separate cacheable requests rather than base64 in
// the bundle.
//
// require() rather than an ESM default import: tsconfig is module=commonjs with
// no esModuleInterop, so `import x from` compiles to require(...).default -- and
// file-loader exports the URL as `module.exports = "..."`, which has no .default.
// That would silently yield undefined instead of a path.
const bgImage: string = require('../../assets/img/bg-cs.png');
const logoImage: string = require('../../assets/img/logo-cs.png');

// Visual-only redesign, keyed to the supplied brand assets.
//
// The form's structure, fields, order, validation and submit flow are unchanged
// from the original -- only presentation.
//
// Both assets are light-toned (and the logo PNG is opaque white with no alpha
// channel), so this is a LIGHT treatment: white text over that background would
// be unreadable, and the logo would render as a white slab on dark glass. The
// background art is composed with its illustration on the right and open space
// on the left, so the card is left-aligned to sit in that space rather than
// covering the artwork.
//
// Styles live in cssRaw rather than inline style objects because the design
// needs backdrop-filter, :focus-within / :hover states and blend modes -- none
// of which inline React styles can express. Selectors are scoped under
// .cs-login so nothing leaks into the rest of the app.
//
// sha-el-design renders its own markup with typestyle-generated class names we
// cannot target directly, so its inputs and button are restyled via descendant
// element selectors. A few declarations need !important to beat the library's
// own single-class rules; they are marked where used.
cssRaw(`
  .cs-login {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display',
                 'Segoe UI', system-ui, Roboto, sans-serif;
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    /* Card to the left, in the background art's open area. */
    justify-content: flex-start;
    padding: 32px clamp(24px, 7vw, 104px);
    overflow: hidden;
    /* Solid colour underneath: the art is a photo-style PNG, and without a base
       colour a slow load or a viewport wider than the image shows white bands. */
    background-color: #EAF3FD;
    background-image: url(${bgImage});
    background-size: cover;
    background-position: center right;
    background-repeat: no-repeat;
    -webkit-font-smoothing: antialiased;
  }

  /* ---------- frosted card ---------- */
  .cs-login__card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 428px;
    padding: 36px 38px 30px;
    border-radius: 22px;
    border: 1px solid rgba(255,255,255,0.95);
    /* Near-opaque: the logo's white ground has to disappear into it, and form
       text must stay legible wherever the art sits behind the card. */
    background: rgba(255,255,255,0.90);
    -webkit-backdrop-filter: blur(18px) saturate(120%);
    backdrop-filter: blur(18px) saturate(120%);
    box-shadow:
      0 26px 60px rgba(23,54,105,0.18),
      0 8px 20px rgba(23,54,105,0.10),
      inset 0 1px 0 rgba(255,255,255,0.90);
  }

  /* ---------- brand ---------- */
  .cs-login__brand {
    margin-bottom: 24px;
  }
  .cs-login__logo {
    display: block;
    width: 210px;
    max-width: 100%;
    height: auto;
    /* The source PNG is 8-bit indexed with NO alpha channel -- its ground is
       opaque white. multiply against the light card makes that white vanish
       (white is the identity for multiply) instead of showing as a slab. If a
       browser lacks blend-mode support the fallback is white-on-near-white,
       which is barely perceptible rather than broken. */
    mix-blend-mode: multiply;
  }

  /* ---------- heading (text unchanged: "LOGIN") ---------- */
  .cs-login__card h1 {
    margin: 0 0 4px;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: 0.055em;
    color: #0F2547;
  }
  .cs-login__hint {
    margin: 0 0 24px;
    font-size: 13.5px;
    color: #52658A;
  }

  /* ---------- inputs (sha-el-design internals) ----------
     The library (pinned 0.0.86) renders each field as:

       div[class^=container_]        <- the library's OWN bordered box
         span[class^=seudo_] > svg   <- the "before" icon, a sibling of section
         section[class^=section_]
           label[class^=label_]      <- floating label
           input[class^=input_]

     Giving the <input> its own border/background draws a SECOND rounded rect
     inside the library's, with the icon stranded in the gap between them. So the
     CONTAINER is styled as the visible field and the input is made completely
     transparent inside it.

     The hashed prefixes come from typestyle debug names and are stable for a
     pinned library version. If a future upgrade changes them these selectors
     stop matching, and the field falls back to the library's own single box --
     degraded, but never double-boxed or broken. */
  .cs-login__card [class^='container_'] {
    background: #fff !important;
    border: 1px solid #CBDAEE !important;
    border-radius: 12px !important;
    transition: border-color .22s ease, box-shadow .22s ease, background .22s ease;
  }
  .cs-login__card [class^='container_']:hover {
    border-color: #9CB9DC !important;
  }
  /* Focus ring on the whole field. Uses :focus-within rather than :focus on the
     input, because the border now lives on the ancestor -- and :focus-within is
     far better supported than :has(). Also the visible keyboard focus ring. */
  .cs-login__card [class^='container_']:focus-within {
    border-color: #2563EB !important;
    box-shadow: 0 0 0 4px rgba(37,99,235,0.16) !important;
  }

  .cs-login__card label {
    /* sha-el-design gives the floating label an opaque white background to notch
       the input border. That is harmless here (the field IS white), but it is
       neutralised anyway so the label never paints a slab over the card. */
    color: #40567A !important;
    background: transparent !important;
    font-size: 13px;
    letter-spacing: 0.015em;
  }
  /* The required marker is an inline style="color: red" on a nested span; only
     !important can override it. Pure red on white measures 4.0:1, under AA. */
  .cs-login__card label span {
    color: #C81E1E !important;
  }

  .cs-login__card input {
    color: #0F2547 !important;                   /* library sets its own colour */
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 12px 2px !important;
    font-size: 15px;
  }
  .cs-login__card input::placeholder { color: #8CA0BD; }

  /* The field icons are react-icons with fill="currentColor". Set fill as well
     as color: a CSS fill beats the presentation attribute outright.
     Scoped to the field containers so the logo image is untouched. */
  .cs-login__card [class^='container_'] svg {
    color: #5B7BA6 !important;
    fill: #5B7BA6 !important;
  }

  /* ---------- primary button ---------- */
  .cs-login__card button {
    width: 100%;
    margin-top: 8px;
    height: 48px;
    border: none !important;
    border-radius: 12px !important;
    /* Deliberately darker than the logo's vivid blue: white at 14.5px bold needs
       4.5:1, and white on that blue measures only ~3.5:1. This ramp measures
       5.5:1 at the light end and 8.7:1 at the dark end. */
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%) !important;
    color: #fff !important;
    font-family: inherit;
    font-size: 14.5px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(30,64,175,0.26);
    transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
  }
  .cs-login__card button:hover {
    transform: translateY(-2px);
    filter: brightness(1.07);
    box-shadow: 0 16px 32px rgba(30,64,175,0.34);
  }
  .cs-login__card button:active { transform: translateY(0); }
  .cs-login__card button:focus-visible {
    outline: 2px solid #1E40AF;
    outline-offset: 3px;
  }

  .cs-login__footer {
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px solid rgba(15,37,71,0.10);
    font-size: 11.5px;
    letter-spacing: 0.05em;
    color: #64779A;
    text-align: center;
  }

  /* Below the point where the art's open left area still exists, centre the card
     and re-centre the background so the illustration is not cropped away. */
  @media (max-width: 900px) {
    .cs-login {
      justify-content: center;
      background-position: center center;
    }
  }
  @media (max-width: 560px) {
    .cs-login { padding: 18px; }
    .cs-login__card { padding: 28px 22px 24px; border-radius: 18px; }
    .cs-login__card h1 { font-size: 23px; }
    .cs-login__logo { width: 178px; }
  }
`);

export class Login extends React.Component<Props, State> {

  @Inject userService: UserService;
  constructor(props: Props) {
    super(props);

    this.state = {
      error: null,
      loading: false,
      value: userSchema.initialize() as any,
    };
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = this.state;
    value[e.target.name] = e.target.value;
    this.setState({ value });
  }

  onSubmit = () => {
    const error = userSchema.validate(this.state.value);
    this.setState({ error });
    this.setState({ loading: true });
    this.userService.login(
      this.state.value,
      (res) => {
        this.setState({ loading: false });
        this.props.history.push('/dashboard');
      },
      (err) => this.setState({ error: { password: err.response, username: '' }, loading: false }),
    );
  }

  render() {
    return (
      <div className='cs-login'>
        <div className='cs-login__card'>
          <div className='cs-login__brand'>
            {/* Real alt text -- the previous inline-SVG mark was aria-hidden, so
                the brand was invisible to assistive tech. */}
            <img
              className='cs-login__logo'
              src={logoImage}
              alt='Calling Soul -- Connect, Converse, Convert'
            />
          </div>
          <h1>LOGIN</h1>
          <p className='cs-login__hint'>Sign in to continue to your workspace</p>
          <Loading
            isLoading={this.state.loading}
            render={() => (
              <div>
                <Input
                  required
                  label='Username'
                  before={<FaUser />}
                  value={this.state.value.username}
                  name='username'
                  onChange={this.onChange}
                  error={nestedAccess(this.state.error, 'username')}
                />
                <Input
                  required
                  type='password'
                  label='Password'
                  before={<FaPassport />}
                  error={nestedAccess(this.state.error, 'password')}
                  value={this.state.value.password}
                  name='password'
                  onChange={this.onChange}
                />
                <Button
                  style={{ width: '100%' }}
                  type='primary'
                  onClick={this.onSubmit}
                >
                  Submit
                </Button>
              </div>
            )}
          />
          <div className='cs-login__footer'>MINU&nbsp;MARKETING&nbsp;PVT&nbsp;LTD&nbsp;&middot;&nbsp;TELE&nbsp;PRO</div>
        </div>
      </div>
    );
  }
}

interface Props {
  history: History;
}

interface State {
  value: {
    username: string;
    password: string;
  };
  error: {
    username: string;
    password: string;
  };
  loading: boolean;
}
